<template>
  <div class="space-y-4">
    <!-- Signature Method Tabs -->
    <div class="flex border border-neutral-light rounded-lg overflow-hidden">
      <button
        type="button"
        @click="signatureMethod = 'pad'"
        :class="signatureMethod === 'pad' ? 'bg-primary text-white' : 'bg-gray-50 text-gray-700'"
        class="flex-1 px-4 py-2 text-sm font-medium transition-colors"
      >
        {{ labels.drawSignature }}
      </button>
      <button
        type="button"
        @click="signatureMethod = 'upload'"
        :class="signatureMethod === 'upload' ? 'bg-primary text-white' : 'bg-gray-50 text-gray-700'"
        class="flex-1 px-4 py-2 text-sm font-medium transition-colors"
      >
        {{ labels.uploadImage }}
      </button>
    </div>

    <!-- Signature Pad -->
    <div v-show="signatureMethod === 'pad'" class="border border-neutral-light rounded-lg overflow-hidden">
      <div class="relative">
        <canvas
          ref="canvasRef"
          class="w-full h-48 bg-white cursor-crosshair"
          style="touch-action: none;"
        ></canvas>
        <div class="absolute top-2 right-2">
          <button
            type="button"
            @click="clearSignature"
            class="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600 transition-colors"
          >
            {{ labels.clear }}
          </button>
        </div>
      </div>
      <div class="px-4 py-2 bg-gray-50 border-t border-neutral-light text-xs text-gray-600">
        {{ labels.signToConfirm }}
      </div>
    </div>

    <!-- File Upload -->
    <div v-show="signatureMethod === 'upload'" class="border border-neutral-light rounded-lg overflow-hidden">
      <div class="p-4">
        <div
          class="border-2 border-dashed rounded-lg p-6 text-center transition-colors"
          :class="isDragOver ? 'border-primary bg-primary/5' : 'border-gray-300'"
          @drop.prevent="handleDrop"
          @dragover.prevent="isDragOver = true"
          @dragenter.prevent="isDragOver = true"
          @dragleave="onDragLeave"
        >
          <input
            ref="fileInputRef"
            type="file"
            @change="handleFileUpload"
            accept="image/*"
            class="hidden"
          />

          <div v-if="!uploadedImage" class="space-y-2">
            <svg class="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
              <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <div class="text-sm text-gray-600">
              <button type="button" class="font-medium text-primary hover:text-primary-dark" @click="fileInputRef?.click()">
                {{ labels.uploadSignatureImage }}
              </button>
              <p class="pl-1">{{ labels.dragAndDrop }}</p>
            </div>
            <p class="text-xs text-gray-500">{{ labels.fileTypes }}</p>
          </div>

          <div v-else class="space-y-2">
            <img :src="uploadedImage" alt="" class="mx-auto max-h-32 border border-gray-200 rounded" />
            <button
              type="button"
              @click="clearUploadedImage"
              class="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600 transition-colors"
            >
              {{ labels.remove }}
            </button>
          </div>
        </div>
      </div>
      <div class="px-4 py-2 bg-gray-50 border-t border-neutral-light text-xs text-gray-600">
        {{ labels.uploadToConfirm }}
      </div>
    </div>

    <span v-if="error" class="block mt-1 text-xs text-red-500">{{ error }}</span>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import SignaturePadLib from 'signature_pad';

const props = defineProps({
  modelValue: { type: String, default: '' },
  error: { type: String, default: '' },
  labels: {
    type: Object,
    default: () => ({
      drawSignature: 'Draw Signature',
      uploadImage: 'Upload Image',
      clear: 'Clear',
      signToConfirm: 'Sign above to confirm your identity',
      uploadSignatureImage: 'Upload a signature image',
      dragAndDrop: 'or drag and drop',
      fileTypes: 'PNG, JPG, GIF up to 5MB',
      remove: 'Remove',
      uploadToConfirm: 'Upload an image of your signature',
    }),
  },
});

const emit = defineEmits(['update:modelValue']);

const canvasRef = ref(null);
const fileInputRef = ref(null);
const signatureMethod = ref('pad');
const uploadedImage = ref(null);
const isDragOver = ref(false);

let padInstance = null;
let resizeHandler = null;

function resizeCanvas() {
  const canvas = canvasRef.value;
  if (!canvas) return;

  const ratio = Math.max(window.devicePixelRatio || 1, 1);
  const rect = canvas.getBoundingClientRect();

  canvas.width = rect.width * ratio;
  canvas.height = rect.height * ratio;
  canvas.style.width = rect.width + 'px';
  canvas.style.height = rect.height + 'px';
  canvas.getContext('2d').scale(ratio, ratio);

  if (padInstance && !padInstance.isEmpty()) {
    const data = padInstance.toData();
    padInstance.clear();
    padInstance.fromData(data);
  }
}

function initPad() {
  const canvas = canvasRef.value;
  if (!canvas) return;

  padInstance = new SignaturePadLib(canvas, {
    backgroundColor: 'rgb(255, 255, 255)',
    penColor: 'rgb(0, 0, 0)',
    minWidth: 1,
    maxWidth: 2.5,
    throttle: 16,
    velocityFilterWeight: 0.7,
  });

  padInstance.addEventListener('endStroke', () => {
    const data = padInstance.toDataURL();
    emit('update:modelValue', data);
  });

  resizeCanvas();

  resizeHandler = () => resizeCanvas();
  window.addEventListener('resize', resizeHandler);
}

function clearSignature() {
  if (padInstance) {
    padInstance.clear();
  }
  emit('update:modelValue', '');
}

function getSignatureData() {
  if (signatureMethod.value === 'pad') {
    if (padInstance && !padInstance.isEmpty()) {
      return padInstance.toDataURL();
    }
    return '';
  }
  return uploadedImage.value || '';
}

function processFile(file) {
  if (!file.type.startsWith('image/')) return;
  if (file.size > 5 * 1024 * 1024) return;

  const reader = new FileReader();
  reader.onload = (e) => {
    uploadedImage.value = e.target.result;
    emit('update:modelValue', e.target.result);
  };
  reader.readAsDataURL(file);
}

function handleFileUpload(event) {
  const file = event.target.files[0];
  if (file) processFile(file);
}

function handleDrop(event) {
  isDragOver.value = false;
  const files = event.dataTransfer.files;
  if (files.length > 0) processFile(files[0]);
}

function onDragLeave(event) {
  if (!event.currentTarget.contains(event.relatedTarget)) {
    isDragOver.value = false;
  }
}

function clearUploadedImage() {
  uploadedImage.value = null;
  emit('update:modelValue', '');
  if (fileInputRef.value) fileInputRef.value.value = '';
}

onMounted(() => {
  nextTick(() => initPad());
});

onUnmounted(() => {
  if (resizeHandler) window.removeEventListener('resize', resizeHandler);
  if (padInstance) padInstance.off();
});

defineExpose({ getSignatureData, clearSignature });
</script>
